/* =========================================================================
   GROFIE GENIE — CHAT WIDGET
   Floating culinary assistant. Talks to the Genie backend
   (POST {backendUrl}/v1/genie/chat), renders a recipe reply plus
   catalog product recommendations. "Add" buttons reuse the theme's
   existing grofie-cart.js flow (data-variant-id + .grofie-card__quick-add),
   so no separate cart integration is needed here.

   Self-contained: no imports, no theme dependencies beyond the shared
   window.GenieConfig object injected by scripts.liquid. Fails silent /
   graceful so it can never break the surrounding live page.
   ========================================================================= */
(function () {
  'use strict';

  const CFG = (window.GenieConfig || {});
  const BACKEND_URL = (CFG.backendUrl || '').replace(/\/+$/, '');
  const ENDPOINT = BACKEND_URL ? BACKEND_URL + '/v1/genie/chat' : '';
  const DETAILS_ENDPOINT = BACKEND_URL ? BACKEND_URL + '/v1/genie/generate' : '';

  // If no backend configured, do nothing at all.
  if (!ENDPOINT) return;

  // Present only on product pages (injected by scripts.liquid).
  const CURRENT_PRODUCT = CFG.currentProduct || null;

  const STRINGS = Object.assign({
    title: 'Grofie Genie',
    subtitle: 'Your culinary helper',
    placeholder: 'Ask for a recipe or meal idea…',
    greeting: "Hi! I'm Genie. Tell me a dish you'd like to cook and I'll share the recipe plus the ingredients you can add to your basket.",
    send: 'Send',
    open: 'Open Grofie Genie chat',
    close: 'Close chat',
    error: "Sorry, I couldn't reach the kitchen just now. Please try again in a moment.",
    add: 'Add',
    added: 'Added',
    ingredientsHeading: 'Shop the ingredients',
    thinking: 'Genie is thinking…'
  }, CFG.strings || {});

  const MAX_HISTORY = 8;
  const STORAGE_KEY = 'grofie:genie:session';
  const STORAGE_MAX = 30; // cap stored turns to keep storage small

  const state = {
    open: false,
    busy: false,
    history: [],    // [{role, content}] — trimmed context sent to the API
    transcript: [], // [{role, html|text}] — full visible conversation for re-render
    mounted: false
  };

  let root, panel, messagesEl, formEl, inputEl, launcherEl;

  // ---- Session persistence (survives navigation within the tab) ----
  function loadSession() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function saveSession() {
    try {
      // Persist the full turn list (role, content, and rendered bot HTML)
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.transcript.slice(-STORAGE_MAX)));
    } catch (e) { /* storage full / disabled — non-fatal */ }
  }

  function clearSession() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
    state.transcript = [];
    state.history = [];
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function money(amount, currency) {
    if (amount == null) return '';
    const num = Number(amount);
    if (!isFinite(num)) return '';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(num);
    } catch (e) {
      return (currency ? currency + ' ' : '') + num.toFixed(2);
    }
  }

  function build() {
    if (state.mounted) return;
    state.mounted = true;

    root = document.createElement('div');
    root.className = 'genie';
    root.setAttribute('data-genie-root', '');

    root.innerHTML = [
      '<button type="button" class="genie__launcher" data-genie-launch aria-label="' + esc(STRINGS.open) + '" aria-expanded="false">',
      '  <span class="genie__launcher-icon" aria-hidden="true">',
      '    <svg viewBox="0 0 24 24" width="28" height="28" fill="#fff" stroke="none"><path d="M17.5 21h-11a1 1 0 0 1-1-1v-4.6a4.5 4.5 0 0 1-1-8.2A5 5 0 0 1 12 4.2 5 5 0 0 1 19.5 7.2a4.5 4.5 0 0 1-1 8.2V20a1 1 0 0 1-1 1zM9 15.2a.9.9 0 0 0-1.8 0v2.3a.9.9 0 0 0 1.8 0zm3.9 0a.9.9 0 0 0-1.8 0v2.3a.9.9 0 0 0 1.8 0zm3.9 0a.9.9 0 0 0-1.8 0v2.3a.9.9 0 0 0 1.8 0z"/></svg>',
      '  </span>',
      '</button>',
      '<section class="genie__panel" data-genie-panel role="dialog" aria-modal="false" aria-label="' + esc(STRINGS.title) + '" hidden>',
      '  <header class="genie__head">',
      '    <div class="genie__head-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 20h10M7 14.5V20h10v-5.5"/><path d="M17 14.5a3.5 3.5 0 0 0 1.2-6.8 4 4 0 0 0-7.6-1.6A4 4 0 0 0 5.8 7.7 3.5 3.5 0 0 0 7 14.5z"/><path d="M9.5 14v2M12 14v2M14.5 14v2"/></svg></div>',
      '    <div class="genie__head-text"><strong>' + esc(STRINGS.title) + '</strong><span>' + esc(STRINGS.subtitle) + '</span></div>',
      '    <button type="button" class="genie__reset" data-genie-reset aria-label="Start a new chat" title="New chat"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg></button>',
      '    <button type="button" class="genie__close" data-genie-close aria-label="' + esc(STRINGS.close) + '"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>',
      '  </header>',
      '  <div class="genie__messages" data-genie-messages aria-live="polite"></div>',
      '  <form class="genie__form" data-genie-form>',
      '    <input type="text" class="genie__input" data-genie-input placeholder="' + esc(STRINGS.placeholder) + '" autocomplete="off" maxlength="500" />',
      '    <button type="submit" class="genie__send" aria-label="' + esc(STRINGS.send) + '"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12l16-8-6 16-3-6-7-2z"/></svg></button>',
      '  </form>',
      '</section>'
    ].join('');

    document.body.appendChild(root);

    panel = root.querySelector('[data-genie-panel]');
    messagesEl = root.querySelector('[data-genie-messages]');
    formEl = root.querySelector('[data-genie-form]');
    inputEl = root.querySelector('[data-genie-input]');
    launcherEl = root.querySelector('[data-genie-launch]');

    launcherEl.addEventListener('click', toggle);
    root.querySelector('[data-genie-close]').addEventListener('click', close);
    root.querySelector('[data-genie-reset]').addEventListener('click', resetChat);
    formEl.addEventListener('submit', onSubmit);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.open) close();
    });

    restoreOrGreet();
  }

  // Rebuild the conversation from the saved session, or show the greeting.
  function restoreOrGreet() {
    const saved = loadSession();
    if (saved.length) {
      state.transcript = saved;
      saved.forEach(function (turn) {
        renderTurn(turn, /* persist */ false);
        // Rebuild the API context from user/assistant turns.
        if (turn.role === 'user' && turn.text) {
          state.history.push({ role: 'user', content: turn.text });
        } else if (turn.role === 'assistant' && turn.reply) {
          state.history.push({ role: 'assistant', content: String(turn.reply).slice(0, 1200) });
        }
      });
      state.history = state.history.slice(-MAX_HISTORY);
    } else {
      // Greeting is not persisted; it's always shown for a fresh session.
      appendMessage('assistant', renderReplyHtml({ reply: STRINGS.greeting }), true);
    }
    // On a product page, always offer the "know more" chip (not persisted —
    // it reflects whatever product page the user is currently on).
    maybeShowProductChip();
  }

  function resetChat() {
    clearSession();
    messagesEl.innerHTML = '';
    appendMessage('assistant', renderReplyHtml({ reply: STRINGS.greeting }), true);
    maybeShowProductChip();
  }

  // Render a tappable "Know more about <product>" chip when on a product page.
  function maybeShowProductChip() {
    if (!CURRENT_PRODUCT || !CURRENT_PRODUCT.title || !DETAILS_ENDPOINT) return;
    const wrap = document.createElement('div');
    wrap.className = 'genie__msg genie__msg--bot genie__chip-row';
    wrap.innerHTML =
      '<button type="button" class="genie__chip" data-genie-details-chip>' +
      '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>' +
      '<span>Know more &amp; origin of ' + esc(truncate(CURRENT_PRODUCT.title, 40)) + '</span></button>';
    messagesEl.appendChild(wrap);
    wrap.querySelector('[data-genie-details-chip]').addEventListener('click', function () {
      wrap.remove();
      requestProductDetails();
    });
    scrollToBottom();
  }

  function truncate(s, n) {
    s = String(s || '');
    return s.length > n ? s.slice(0, n - 1) + '…' : s;
  }

  // Render a saved turn back into the DOM without re-persisting it.
  function renderTurn(turn, persist) {
    if (turn.role === 'user') {
      appendMessage('user', turn.text || '', false, persist);
    } else {
      appendMessage('assistant', turn.html || '', true, persist);
    }
  }

  function toggle() { state.open ? close() : open(); }

  function open() {
    state.open = true;
    panel.hidden = false;
    // next frame for transition
    requestAnimationFrame(function () { root.classList.add('genie--open'); });
    launcherEl.setAttribute('aria-expanded', 'true');
    setTimeout(function () { try { inputEl.focus(); } catch (e) {} }, 60);
  }

  function close() {
    state.open = false;
    root.classList.remove('genie--open');
    launcherEl.setAttribute('aria-expanded', 'false');
    setTimeout(function () { if (!state.open) panel.hidden = true; }, 220);
  }

  function scrollToBottom() {
    try { messagesEl.scrollTop = messagesEl.scrollHeight; } catch (e) {}
  }

  function appendMessage(role, html, isHtml) {
    const wrap = document.createElement('div');
    wrap.className = 'genie__msg genie__msg--' + (role === 'user' ? 'user' : 'bot');
    const bubble = document.createElement('div');
    bubble.className = 'genie__bubble';
    if (isHtml) bubble.innerHTML = html;
    else bubble.textContent = html;
    wrap.appendChild(bubble);
    messagesEl.appendChild(wrap);
    scrollToBottom();
    return wrap;
  }

  function renderReplyHtml(result) {
    const parts = [];
    if (result.reply) {
      parts.push('<div class="genie__reply">' + esc(result.reply).replace(/\n/g, '<br>') + '</div>');
    }
    if (result.recipe && Array.isArray(result.recipe.steps) && result.recipe.steps.length) {
      const r = result.recipe;
      parts.push('<div class="genie__recipe">');
      parts.push('<div class="genie__recipe-name">' + esc(r.name || 'Recipe') +
        (r.servings ? ' <span class="genie__recipe-serves">' + esc(r.servings) + '</span>' : '') + '</div>');
      parts.push('<ol class="genie__recipe-steps">');
      r.steps.forEach(function (step) { parts.push('<li>' + esc(step) + '</li>'); });
      parts.push('</ol></div>');
    }
    return parts.join('');
  }

  function renderProducts(recs) {
    if (!Array.isArray(recs) || !recs.length) return '';
    const out = ['<div class="genie__shop"><div class="genie__shop-head">' + esc(STRINGS.ingredientsHeading) + '</div>'];
    recs.forEach(function (group) {
      if (!group || !Array.isArray(group.products) || !group.products.length) return;
      out.push('<div class="genie__ingredient">' + esc(group.ingredient) + '</div>');
      out.push('<div class="genie__products">');
      group.products.forEach(function (p) { out.push(productCard(p)); });
      out.push('</div>');
    });
    out.push('</div>');
    return out.join('');
  }

  function productCard(p) {
    const price = money(p.price, p.currency);
    const img = p.image
      ? '<img class="genie__prod-img" src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="48" height="48">'
      : '<span class="genie__prod-img genie__prod-img--empty" aria-hidden="true"></span>';
    // Reuse theme cart flow: grofie-cart.js delegates on [data-grofie-add] and
    // reads data-variant-id. Both attributes are required for the click to fire.
    const canAdd = p.variantId && p.available !== false;
    const addBtn = canAdd
      ? '<button type="button" class="genie__add grofie-card__quick-add" data-grofie-add data-variant-id="' + esc(p.variantId) + '" data-quantity="1" aria-label="' + esc(STRINGS.add) + ' ' + esc(p.title) + '">' + esc(STRINGS.add) + '</button>'
      : (p.available === false
          ? '<span class="genie__add genie__add--out">Out of stock</span>'
          : '<a class="genie__add genie__add--link" href="' + esc(p.url || '#') + '">View</a>');

    return [
      '<div class="genie__prod">',
      '  <a class="genie__prod-main" href="' + esc(p.url || '#') + '">',
      img,
      '    <span class="genie__prod-info"><span class="genie__prod-title">' + esc(p.title) + '</span>' +
           (price ? '<span class="genie__prod-price">' + esc(price) + '</span>' : '') + '</span>',
      '  </a>',
      addBtn,
      '</div>'
    ].join('');
  }

  function setBusy(busy) {
    state.busy = busy;
    inputEl.disabled = busy;
    formEl.classList.toggle('genie__form--busy', busy);
  }

  function showTyping() {
    const wrap = document.createElement('div');
    wrap.className = 'genie__msg genie__msg--bot genie__typing';
    wrap.setAttribute('data-genie-typing', '');
    wrap.innerHTML = '<div class="genie__bubble"><span class="genie__dots"><i></i><i></i><i></i></span></div>';
    messagesEl.appendChild(wrap);
    scrollToBottom();
    return wrap;
  }

  function onSubmit(e) {
    e.preventDefault();
    if (state.busy) return;
    const text = (inputEl.value || '').trim();
    if (!text) return;

    inputEl.value = '';
    appendMessage('user', text, false);
    state.history.push({ role: 'user', content: text });
    state.transcript.push({ role: 'user', text: text });
    saveSession();

    send(text);
  }

  async function send(text) {
    setBusy(true);
    const typing = showTyping();

    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: state.history.slice(-MAX_HISTORY)
        })
      });

      typing.remove();

      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      const result = (data && data.result) || {};

      const html = renderReplyHtml(result) + renderProducts(result.productRecommendations);
      appendMessage('assistant', html || esc(STRINGS.error), true);

      if (result.reply) {
        state.history.push({ role: 'assistant', content: String(result.reply).slice(0, 1200) });
      }
      // Persist the rendered turn so it survives page navigation.
      state.transcript.push({ role: 'assistant', html: html || esc(STRINGS.error), reply: result.reply || '' });
      saveSession();
    } catch (err) {
      if (typing && typing.parentNode) typing.remove();
      appendMessage('assistant', esc(STRINGS.error), true);
      // eslint-disable-next-line no-console
      console.warn('[genie] chat request failed:', err);
    } finally {
      setBusy(false);
      try { inputEl.focus(); } catch (e) {}
    }
  }

  // Fetch AI product details/origin/storage/nutrition for the current product
  // and render them as a chat message.
  async function requestProductDetails() {
    if (state.busy || !CURRENT_PRODUCT) return;
    if (!state.open) open();

    appendMessage('user', 'Tell me more about ' + CURRENT_PRODUCT.title, false);
    setBusy(true);
    const typing = showTyping();

    try {
      const res = await fetch(DETAILS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(CURRENT_PRODUCT)
      });
      typing.remove();
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      const result = (data && data.result) || {};
      const html = renderProductDetails(result);
      appendMessage('assistant', html || esc(STRINGS.error), true);
      // Not persisted to session: details are page-specific and cheap to re-fetch.
    } catch (err) {
      if (typing && typing.parentNode) typing.remove();
      appendMessage('assistant', esc(STRINGS.error), true);
      console.warn('[genie] product details failed:', err);
    } finally {
      setBusy(false);
    }
  }

  function renderProductDetails(result) {
    var out = [];
    out.push('<div class="genie__pd-title">' + esc(CURRENT_PRODUCT.title) + '</div>');
    if (result.details) {
      out.push('<div class="genie__reply">' + esc(result.details).replace(/\n/g, '<br>') + '</div>');
    }
    var block = function (heading, items) {
      if (!Array.isArray(items) || !items.length) return;
      out.push('<div class="genie__pd-block"><div class="genie__pd-head">' + esc(heading) + '</div><ul>');
      items.forEach(function (i) { out.push('<li>' + esc(i) + '</li>'); });
      out.push('</ul></div>');
    };
    block('Recipe ideas', result.recipes);
    block('Storage tips', result.storageTips);
    block('Good to know', result.nutritionHighlights);
    return out.join('');
  }

  function init() {
    try { build(); } catch (e) { console.warn('[genie] init failed:', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
